import React from "react"

import { graphql } from "gatsby"
import PropTypes from "prop-types"

import Seo from "../components/head"
import Layout from "../components/layout"
import Feed from "../components/organisms/feed"
import SubscribeHome from "../components/organisms/subscribe-home"

const nextPage = "/stories/2"

const IndexPage = ({ data, pageContext }) => {
  const stories = data.allSanityArticle.nodes
  const regionFeatured = data.sanitySettings.featured_cards
    ?.filter((card) => card.country?.abbreviation !== pageContext.country)
    .map((card) => card.card)

  if (stories.length > 12 && regionFeatured.length > 0) {
    regionFeatured.forEach((featuredArticle) => {
      stories.splice(
        stories.indexOf(
          stories.find((story) => featuredArticle.id === story.id)
        ),
        1
      )
    })
  }

  const sidebar = data.guides.nodes.concat(
    data.faqs.nodes,
    data.glossaries.nodes
  )

  return (
    <Layout>
      <SubscribeHome home data={data} />
      <Feed
        stories={stories}
        featuredArticles={regionFeatured}
        sidebar={sidebar}
        type="home"
        testimonials
        podcasts={data.allSanityPodcast.nodes}
        nextPage={nextPage}
      />
    </Layout>
  )
}

export default IndexPage

const HeadComponent = ({ location }) => (
  <Seo path={location.pathname} nextPage={nextPage} isAd />
)

HeadComponent.propTypes = {
  location: PropTypes.object.isRequired,
}

export const Head = HeadComponent

IndexPage.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
}

export const IndexPageQuery = graphql`
  query homeQuery($country: String!) {
    sanitySettings {
      featured_cards {
        country {
          abbreviation
        }
        card {
          ... on SanityPage {
            title
            image {
              ...ImageWithPreview
            }
            abstract: description
            type: _type
            slug {
              current
            }
            presenting_partners {
              country {
                abbreviation
              }
              partner {
                prefix
                name
                logo {
                  ...ImageWithPreview
                }
                dark_logo {
                  ...ImageWithPreview
                }
              }
            }
          }
          ... on SanityBracket {
            title
            image {
              ...ImageWithPreview
            }
            abstract: description
            type: _type
            slug {
              current
            }
            date: start_date
            presenting_partners {
              country {
                abbreviation
              }
              partner {
                prefix
                name
                logo {
                  ...ImageWithPreview
                }
                dark_logo {
                  ...ImageWithPreview
                }
              }
            }
          }
          ... on SanityPodcast {
            type: _type
            episode_number
            date
            title
            _rawAbstract: _rawSummary
            slug {
              current
            }
            image {
              ...ImageWithPreview
            }
          }
          ... on SanityContest {
            type: _type
            image {
              ...ImageWithPreview
            }
            title: name
            abstract
            date: end_date
            slug {
              current
            }
          }
          ... on SanityQuiz {
            id
            title
            type: _type
            slug {
              current
            }
            partner {
              prefix
              name
              logo {
                ...ImageWithPreview
              }
              dark_logo {
                ...ImageWithPreview
              }
            }
            partner_url
            abstract
            image {
              ...ImageWithPreview
            }
          }
          ... on SanityFaqGlossary {
            id
            faqGlossarySport: sport {
              name
              slug {
                current
              }
            }
            abstract
            image: featured_image {
              ...ImageWithPreview
            }
            type
            _type
          }
          ... on SanityArticle {
            id
            country {
              name
            }
            abstract
            date
            detailed_title
            title
            featured_image {
              image {
                ...ImageWithPreview
              }
            }
            sport {
              slug {
                current
              }
              emoji
              color
              name
            }
            slug {
              current
            }
            partner {
              prefix
              name
              logo {
                ...ImageWithPreview
              }
              dark_logo {
                ...ImageWithPreview
              }
            }
            web_article_type
            vertical {
              color
              name
              slug {
                current
              }
            }
          }
        }
      }
    }
    allSanityArticle(
      limit: 14
      sort: { date: DESC }
      filter: {
        country: { abbreviation: { ne: $country } }
        publishing: { in: "website" }
      }
    ) {
      nodes {
        id
        country {
          name
        }
        abstract
        date
        detailed_title
        title
        featured_image {
          image {
            ...ImageWithPreview
          }
        }
        sport {
          slug {
            current
          }
          emoji
          color
          name
        }
        slug {
          current
        }
        partner {
          prefix
          name
          logo {
            ...ImageWithPreview
          }
          dark_logo {
            ...ImageWithPreview
          }
        }
        web_article_type
        vertical {
          color
          name
          slug {
            current
          }
        }
      }
    }
    guides: allSanityArticle(
      filter: {
        web_article_type: { eq: "guide" }
        publishing: { in: "website" }
        country: { abbreviation: { ne: $country } }
      }
      sort: { date: DESC }
      limit: 3
    ) {
      nodes {
        id
        title
        detailed_title
        featured_image {
          image {
            ...ImageWithPreview
          }
        }
        country {
          abbreviation
        }
        web_article_type
        slug {
          current
        }
      }
    }
    faqs: allSanityFaqGlossary(filter: { type: { eq: "faq" } }, limit: 3) {
      nodes {
        id
        sport {
          name
          slug {
            current
          }
        }
        featured_image {
          ...ImageWithPreview
        }
        type
      }
    }
    glossaries: allSanityFaqGlossary(
      filter: { type: { eq: "glossary" } }
      limit: 3
    ) {
      nodes {
        id
        sport {
          name
          slug {
            current
          }
        }
        featured_image {
          ...ImageWithPreview
        }
        type
      }
    }
    allSanityPodcast(limit: 5, sort: { date: DESC }) {
      nodes {
        type: _type
        date
        episode_number
        title
        image {
          ...ImageWithPreview
        }
        slug {
          current
        }
        _rawSummary
        urls {
          platform
          url
        }
      }
    }
    allSanityDictionary {
      nodes {
        key {
          current
        }
        value
      }
    }
    allSanityCountry {
      nodes {
        abbreviation
        name
      }
    }
    allSanityCity {
      nodes {
        abbreviation
        name
        country {
          abbreviation
        }
      }
    }
    allSanityConference {
      nodes {
        name
      }
    }
    sanitySubscribe(vertical: { slug: { current: { eq: "daily-sports" } } }) {
      title
      vertical {
        cm_id
        color
        slug {
          current
        }
      }
      heading {
        default
        referral
        additional
        growth_channel {
          sqf_channel
          heading
          growth_channel_details
        }
      }
      phone {
        default {
          ...ImageWithPreview
        }
        growth_channel {
          image {
            ...ImageWithPreview
          }
          growth_channel_details
          sqf_channel
        }
        referral {
          ...ImageWithPreview
        }
      }
      description {
        _rawDefault
        _rawReferral
        growth_channel {
          sqf_channel
          growth_channel_details
          _rawDescription
        }
      }
      press_title
      press {
        name
        url
        dark_logo {
          ...ImageWithPreview
        }
        logo {
          ...ImageWithPreview
        }
      }
      ctas {
        cta
        cta_additional
        ctas {
          text
          url
        }
      }
      success {
        title
        video {
          asset {
            url
          }
        }
        _rawDefault
        _rawReferral
        growth_channel {
          sqf_channel
          growth_channel_details
          _rawDescription
        }
      }
      additional_fields {
        name
        tag
        type
        values
      }
    }
  }
`
